import { CHUNK_TYPE, MemberStatus } from '~/constants';
import { isNonEmpty, isNonNil, list, shiftRight } from '~/utils/fnUtil/fnUtil';
import { memoizeCappedWith } from '~/utils/memoizeUtil';
import { lift, mMaybe } from '~/utils/monadUtil';
import { escape, isEmpty, unescape } from 'lodash';
import {
  allPass,
  always,
  cond,
  converge,
  curry,
  defaultTo,
  equals,
  evolve,
  find,
  identity,
  ifElse,
  invoker,
  join,
  length,
  map,
  merge,
  not,
  objOf,
  pipe,
  prop,
  propEq,
  reject,
  replace,
  split,
  sum,
  T,
  take,
  test,
  toLower,
  toUpper,
  unless,
  when,
} from 'ramda';
import ramdaUseWith from 'ramda/es/useWith';
import { Either } from 'ramda-fantasy';

import { emojiConvertor } from '../emojiConvertor';
import _isJsonText from './isJsonText';

export { _isJsonText as isJsonText };

const memoizeChunkParams = pipe(list, join(':'));

export const htmlEntityEncode = memoizeCappedWith((input: string) => {
  const span = document.createElement('span');
  span.innerText = input;
  return span.innerHTML.replace(/\[/g, '&#91;').replace(/\]/g, '&#93;');
});

export const safeParseJsonText = (text) => {
  try {
    const result = JSON.parse(text);
    return Either.Right(result);
  } catch (e) {
    return Either.Left(text);
  }
};

export const replaceMentionToName = (text) => {
  return text.replace(/\[([^[]*?)]\(dooray:\/\/\d+\/\w+\/\d+ "\w+"\)/gim, '$1');
};

export const stringify = (obj) => {
  return JSON.stringify(obj);
};

export const escapeText = memoizeCappedWith(pipe<string, string, string>(unescape, escape));

const escapeRegex = memoizeCappedWith((str: string) => {
  return str.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1').replace(/\s/g, '\\s');
});

export const createRegExp = memoizeCappedWith((str: string) => {
  return new RegExp(escapeRegex(str), 'mgi');
});

export const chunkHighlight = memoizeCappedWith((text, regStr) => {
  if (isEmpty(regStr)) {
    return [{ text, highlight: false }];
  }
  return pipe(
    split(new RegExp(`(${escapeRegex(regStr)})`, 'gmi')),
    unless(pipe(length, equals(1)), reject(isEmpty)),
    map(converge(merge, [objOf('text'), pipe(toLower, equals(toLower(regStr)), objOf('highlight'))]))
  )(text);
}, memoizeChunkParams);

const codeBlockRegExp = /(```.*?\n[\s\S]*?```)/gim;
const inlineCodeRegExp = /(`[\s\S]+?`)/gim;
const mentionRegExp = /(\[@.*?\]\(dooray:\/\/\d+\/\w+\/\d+ "\w+"\))/gim;
// https://tools.ietf.org/html/rfc3986#appendix-A
// https://github.com/jhermsmeier/uri.regex/blob/master/pattern.js
// RFC를 구현한 구현체에서 capture 부분 제거
const urlRegExp =
  /([A-Za-z][A-Za-z0-9+\-.]*:(?:\/\/(?:(?:[A-Za-z0-9\-._~!$&'()*+,;=:]|%[0-9A-Fa-f]{2})*@)?(?:\[(?:(?:(?:(?:[0-9A-Fa-f]{1,4}:){6}|::(?:[0-9A-Fa-f]{1,4}:){5}|(?:[0-9A-Fa-f]{1,4})?::(?:[0-9A-Fa-f]{1,4}:){4}|(?:(?:[0-9A-Fa-f]{1,4}:){0,1}[0-9A-Fa-f]{1,4})?::(?:[0-9A-Fa-f]{1,4}:){3}|(?:(?:[0-9A-Fa-f]{1,4}:){0,2}[0-9A-Fa-f]{1,4})?::(?:[0-9A-Fa-f]{1,4}:){2}|(?:(?:[0-9A-Fa-f]{1,4}:){0,3}[0-9A-Fa-f]{1,4})?::[0-9A-Fa-f]{1,4}:|(?:(?:[0-9A-Fa-f]{1,4}:){0,4}[0-9A-Fa-f]{1,4})?::)(?:[0-9A-Fa-f]{1,4}:[0-9A-Fa-f]{1,4}|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))|(?:(?:[0-9A-Fa-f]{1,4}:){0,5}[0-9A-Fa-f]{1,4})?::[0-9A-Fa-f]{1,4}|(?:(?:[0-9A-Fa-f]{1,4}:){0,6}[0-9A-Fa-f]{1,4})?::)|[Vv][0-9A-Fa-f]+\.[A-Za-z0-9\-._~!$&'()*+,;=:]+)\]|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|(?:[A-Za-z0-9\-._~!$&'()*+,;=]|%[0-9A-Fa-f]{2})*)(?::[0-9]*)?(?:\/(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})*)*|\/(?:(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})+(?:\/(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})*)*)?|(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})+(?:\/(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})*)*)(?:\?(?:[A-Za-z0-9\-._~!$&'()*+,;=:@/?]|%[0-9A-Fa-f]{2})*)?(?:#(?:[A-Za-z0-9\-._~!$&'()*+,;=:@/?]|%[0-9A-Fa-f]{2})*)?)/gim;
const emojiRegExp = /(:[a-z0-9-_+]+:(?::skin-tone-[2-6]:)?)/gim;
const markdownLinkRegExp =
  /(\[[^\][]+\]\((?:http|ftp|https|zpl):\/\/[\w\S-]+(?:\.[\w\S-]+)*([\w\S.,@?^=%&amp;:/~+#-]*[\w@?^=%&amp;/~+#-])?\))/gim;

export const extractCodeAndLang = memoizeCappedWith((input: string) => {
  const matches = /(```)(.*)?\n([\s\S]*?)(```)/gim.exec(input);
  if (isNonEmpty(matches)) {
    const [, , lang = 'nohighlight', code] = matches;
    return { lang, code };
  }
});

const validateEmoji = (text: string): boolean => {
  return isNonNil(emojiConvertor.map.colons[text.substring(1, text.length - 1)]);
};

const validateChunk = (type, text): boolean => {
  return type === CHUNK_TYPE.EMOJI ? validateEmoji(text) : true;
};

const makeChunk = (text: string, type: CHUNK_TYPE = CHUNK_TYPE.TEXT) => {
  return { type, text };
};

const chunkText = curry((type: CHUNK_TYPE, regExp: RegExp, text: string) => {
  if (isEmpty(text) || !text?.matchAll) {
    return [makeChunk(text)];
  }

  const matches = Array.from(text.matchAll(regExp));

  let index = 0;
  const chunkList = [];
  matches.forEach((match) => {
    addTextChunk(match.index);
    const matchText = match[0];
    chunkList.push(validateChunk(type, matchText) ? makeChunk(matchText, type) : makeChunk(matchText));
    index = match.index + matchText.length;
  });
  addTextChunk(text.length);

  function addTextChunk(lastIndex: number) {
    if (index < lastIndex) {
      chunkList.push(makeChunk(text.substring(index, lastIndex)));
    }
  }

  return chunkList;
});

export const extractLabelAndUrl = memoizeCappedWith((input: string) => {
  const urlRegExp =
    /\[(.*?)\]\(((?:http|ftp|https|zpl):\/\/[\w\S-]+(?:\.[\w\S-]+)*([\w\S.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?)\)/gim;
  return pipe(
    lift(identity),
    mMaybe({ label: '', url: '' }, ([, label, url]) => ({ label, url }))
  )(urlRegExp.exec(input));
});

export const chunkCodeBlock = memoizeCappedWith(chunkText(CHUNK_TYPE.CODE_BLOCK, codeBlockRegExp), memoizeChunkParams);
export const chunkInlineCode = memoizeCappedWith(
  chunkText(CHUNK_TYPE.INLINE_CODE, inlineCodeRegExp),
  memoizeChunkParams
);
export const chunkMention = memoizeCappedWith(chunkText(CHUNK_TYPE.MENTION, mentionRegExp), memoizeChunkParams);
export const chunkUrl = memoizeCappedWith(chunkText(CHUNK_TYPE.URL, urlRegExp), memoizeChunkParams);
export const chunkMarkdownLink = memoizeCappedWith((text: string) => {
  if (isEmpty(text)) return [{ text, type: CHUNK_TYPE.TEXT }];

  return pipe(
    split(markdownLinkRegExp),
    reject(isEmpty),
    map(
      converge<any, any, any, any>(merge, [
        objOf('text'),
        pipe(
          ifElse(test(markdownLinkRegExp), always(CHUNK_TYPE.MARKDOWN_LINK), always(CHUNK_TYPE.TEXT)),
          objOf('type')
        ),
      ])
    )
  )(text);
});

export const chunkEmoji = memoizeCappedWith(chunkText(CHUNK_TYPE.EMOJI, emojiRegExp), memoizeChunkParams);

export const validateStatus = (status: string): Either<any, string> => {
  return pipe(
    Object.values,
    reject(equals(MemberStatus.Offline)),
    map(toUpper),
    find(equals(status.toLocaleUpperCase())),
    ifElse(isNonEmpty, Either.Right, Either.Left)
  )(MemberStatus);
};

export const getByteLengthByCharCode = (charCode) => {
  return shiftRight(11, charCode) ? 3 : shiftRight(7, charCode) ? 2 : 1;
};

export const getCharByte = pipe<string, any, number>(invoker(1, 'charCodeAt')(0), getByteLengthByCharCode);

export const getBytesLength = pipe<string, number[], number>(map<string, number[]>(getCharByte), sum);

export const isInRange = curry((low, high, value) => {
  return value >= low && value <= high;
});

export const convert2ByteStringTo = pipe(
  defaultTo(''),
  map(invoker(1, 'charCodeAt')(0)),
  map(
    cond([
      [
        allPass([isInRange(65281, 65374), ramdaUseWith(not, [equals(65340)])]),
        (code: number) => String.fromCharCode(code - 65248),
      ],
      [equals(8217), always(String.fromCharCode(39))],
      [equals(8221), always(String.fromCharCode(34))],
      [equals(12288), always(String.fromCharCode(32))],
      [equals(65507), always(String.fromCharCode(126))],
      [equals(65509), always(String.fromCharCode(92))],
      [T, String.fromCharCode],
    ])
  ),
  join('')
);

export const removeMarkdown = memoizeCappedWith((text: string) => {
  const codeBlockRegExp = /```.*?\n([\s\S]*?)```/gim;
  const inlineCodeRegExp = /`([\s\S]+?)`/gim;
  const mentionRegExp = /\[(@[^[]*?)]\(dooray:\/\/\d+\/\w+\/\d+ "\w+"\)/gim;
  const markdownLinkRegExp =
    /\[(.*?)\]\((?:http|ftp|https|zpl):\/\/[\w\S-]+(?:\.[\w\S-]+)*([\w\S.,@?^=%&amp;:/~+#-]*[\w@?^=%&amp;/~+#-])?\)/gim;

  return pipe<string, string, string, string, string>(
    replace(codeBlockRegExp, '$1'),
    replace(inlineCodeRegExp, '$1'),
    replace(mentionRegExp, '$1'),
    replace(markdownLinkRegExp, '$1')
  )(text);
});

export const ellipsisText = curry((length: number, text: string) => {
  const fixedText = defaultTo('', text);
  return `${take(length, fixedText)}${fixedText.length > length ? '...' : ''}`;
});

export const removeMarkdownToChunk = map<
  { type?: string; text?: string }[],
  { type?: string; removeMarkdown?: string }[]
>(
  when<{ type?: string; text?: string }, { type?: string; removeMarkdown?: string }>(
    propEq('type', CHUNK_TYPE.TEXT),
    evolve({ text: removeMarkdown })
  )
);

export const getTextObj = pipe(prop('text'), JSON.parse);
